import { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { useGet } from 'restful-react'
import { ApiGetRoomResponse, RoomContext, RoomContextType } from '../RoomContext'
import { ClientType } from '../UserContext'
import useRootSnackbar from './useRootSnackbar'
import { AnalyticsEvents, AnalyticsEventType } from './AnalyticsService'
import useAnalytics from './useAnalytics'
import { unstable_batchedUpdates } from 'react-dom'
import useRoomActions from './useRoomActions'
import { logger } from '@/services/Logger'

const log = logger.createChildLogger({ name: 'useJoinRoom' })

function useJoinRoom() {
    const history = useHistory()

    const { dispatch: roomDispatch, state: roomState } = useContext(RoomContext) as RoomContextType

    const [joincode, setJoincode] = useState('')
    const [currentMetadata, setCurrentMetadata] = useState<Record<string, string>>({})
    const [joinSessionErrorMessage, setJoinSessionErrorMessage] = useState('')
    const [joinSessionErrorCode, setJoinSessionErrorCode] = useState('')
    const [showPasswordPrompt, setShowPaswordPrompt] = useState(false)
    const { logUserActivityWithContext } = useAnalytics()

    const RootSnackbar = useRootSnackbar()

    const { loadRoom } = useRoomActions()

    const {
        data: roomData,
        error: joinRoomApiError,
        refetch,
        loading,
    } = useGet<ApiGetRoomResponse>({
        path: '/v1/rooms/',
        lazy: true,
    })

    useEffect(() => {
        if (!joinRoomApiError) {
            return
        }

        log.verbose('joinRoomApiError', '', { joinRoomApiError })

        if (joinRoomApiError.status) {
            // If have status, then at least got in contact with the server.
            if (joinRoomApiError.data?.code === 'password_required') {
                // Show password prompt
                setShowPaswordPrompt(true)
                setJoinSessionErrorMessage('')
            } else if (joinRoomApiError.data?.code === 'not_found') {
                setJoinSessionErrorMessage('Room not found')
            } else if (joinRoomApiError.data?.code === 'password_mismatch') {
                setJoinSessionErrorMessage('Incorrect password')
            } else if (joinRoomApiError.data?.code === 'alert_message') {
                setJoinSessionErrorMessage(joinRoomApiError.data?.message || 'null')
            } else {
                setJoinSessionErrorMessage(`${joinRoomApiError.status}: ${joinRoomApiError.data?.code}`)
            }
            setJoinSessionErrorCode(joinRoomApiError.data?.code ?? '')
        } else {
            // No status - network error
            RootSnackbar.show({
                severity: 'error',
                message: `Could not connect to the server.`,
            })
        }
    }, [joinRoomApiError])

    useEffect(() => {
        if (joincode && joincode.trim().length > 0) {
            setJoinSessionErrorMessage('')
        }
    }, [joincode])

    const onRoomData = async (response: ApiGetRoomResponse | null, metadata?: Record<string, string>) => {
        if (!response || !response.room) {
            return
        }

        if (response.room.createdFromAppVersion == null) {
            // this room was created by a host with app version id older than v1.5.0.
            roomDispatch({
                type: 'update_room_context',
                value: {
                    isRoomAppUpgradeRequiredDialogOpen: { type: 'outdated_room' },
                },
            })
            return
        }

        await loadRoom(response.room, { joinLinkMetadata: metadata })
    }

    const joinRoomWithCode = async (
        _code: string,
        _password?: string,
        metadata?: { [key: string]: string },
        joinOriginEvent: AnalyticsEventType = AnalyticsEvents.JOINED_WITH_ROOM_CODE,
        clientType?: ClientType,
        isLoadOnly?: boolean
    ) => {
        if (_code == null || typeof _code !== 'string' || _code.trim().length === 0) {
            setJoinSessionErrorMessage('Please enter a session link')
            return { success: false, moved: false }
        }

        const code = encodeURIComponent(_code.trim())

        let path = `/v1/rooms/muse:joincode:${code}`
        const codeType = clientType === 'listenonly' ? 'joinuid' : 'joincode'
        if (codeType === 'joinuid') {
            path = `/v1/rooms/muse:joinuid:${code}`
        }

        if (_password) {
            path += `?p=${encodeURIComponent(_password)}`
        }

        unstable_batchedUpdates(() => {
            setJoincode(_code)
            setCurrentMetadata(metadata ?? {})
            setJoinSessionErrorMessage('')
            setJoinSessionErrorCode('')
        })

        logUserActivityWithContext(joinOriginEvent)

        // NOTE: `joinRoomApiError` gets updated on error; only the happy
        // path returns data.
        const response = await refetch({ path })
        if (response) {
            await onRoomData(response, metadata)
            if (!isLoadOnly) {
                history.push('/collaborate')
                return { success: true, moved: true }
            } else {
                return { success: true, moved: false }
            }
        } else {
            return { success: false, moved: false }
        }
    }

    const closePasswordPrompt = () => {
        setShowPaswordPrompt(false)
    }

    return {
        roomData,
        joincode,
        joincodeMetadata: currentMetadata,
        setJoincode,
        joinRoomWithCode,
        joinRoomApiError,
        joinRoomLoading: loading,
        joinSessionErrorMessage,
        joinSessionErrorCode,
        showPasswordPrompt,
        closePasswordPrompt,
    }
}

export default useJoinRoom
